import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Shield, 
  User as UserIcon, 
  Lock, 
  Eye, 
  EyeOff, 
  Building2, 
  Loader2, 
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';
import ForcePasswordChange from './ForcePasswordChange';
import MfaVerification from './MfaVerification';
import { AxiomFlowMark } from '../shared/BrandLogo';

const LoginScreen: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tenantCode, setTenantCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // MFA state
  const [showMfaVerification, setShowMfaVerification] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  
  // SSO state
  const [ssoStatus, setSsoStatus] = useState<{
    ssoEnabled: boolean;
    providerName?: string;
    allowPasswordLogin: boolean;
    enforceSso: boolean;
  } | null>(null);
  const [isCheckingSso, setIsCheckingSso] = useState(false);

  const { login, loginWithToken, isAuthenticated, mustChangePassword, clearMustChangePassword } = useAuth();
  const navigate = useNavigate();

  // Check SSO status
  useEffect(() => {
    const checkSso = async () => {
      if (!tenantCode || tenantCode.length < 2) {
        setSsoStatus(null);
        return;
      }
      setIsCheckingSso(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/sso/status?tenantCode=${tenantCode}`);
        setSsoStatus(response.data);
      } catch {
        setSsoStatus(null);
      } finally {
        setIsCheckingSso(false);
      }
    };
    const timer = setTimeout(checkSso, 500);
    return () => clearTimeout(timer);
  }, [tenantCode]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !mustChangePassword) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, mustChangePassword, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      const result = await login(username, password);
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        setShowMfaVerification(true);
        return;
      }
      if (!result.mustChangePassword) {
        window.location.href = '/';
      }
    } catch (err: any) {
      setError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSsoLogin = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/sso/initiate`, { tenantCode });
      if (response.data.authorizationUrl) {
        sessionStorage.setItem('sso_tenant_code', tenantCode);
        window.location.href = response.data.authorizationUrl;
      } else {
        setError('Failed to initiate SSO login');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'SSO initiation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showMfaVerification) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="glass-card p-8 w-full max-w-md">
          <MfaVerification 
            mfaToken={mfaToken} 
            onSuccess={(res: any) => {
              loginWithToken(res.token, res.user);
              window.location.href = '/';
            }} 
            onCancel={() => setShowMfaVerification(false)} 
          />
        </div>
      </div>
    );
  }

  if (mustChangePassword) {
    return <ForcePasswordChange onPasswordChanged={() => { clearMustChangePassword(); window.location.href = '/'; }} currentPassword={password} />;
  }

  return (
    <div className="min-h-screen bg-slate-950 flex relative overflow-hidden">
      {/* Visual Accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-blue-600/10 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-violet-600/10 blur-[120px] rounded-full" />
      </div>

      {/* Right Panel: Login Form (Now centered) */}
      <div className="flex-1 flex items-center justify-center p-6 relative z-10 w-full">
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          <div className="glass-card p-10 relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-violet-600" />
            
            <div className="mb-10 text-center">
              <Link to="/" aria-label="Back to the AxiomFlow home page" className="inline-flex items-center justify-center gap-3 mb-6 text-white no-underline hover:opacity-80 transition-opacity">
                <AxiomFlowMark size={44} />
                <span className="text-xl font-bold tracking-tight uppercase">AxiomFlow</span>
              </Link>
              <h2 className="text-3xl font-bold text-white mb-2 tracking-tight">Welcome Back</h2>
              <p className="text-slate-500">
                {tenantCode
                  ? `Sign in to the ${tenantCode.toUpperCase()} workspace`
                  : 'Sign in to your organization workspace'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Org Code */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Organization Code</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
                    <Building2 className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    value={tenantCode}
                    onChange={(e) => setTenantCode(e.target.value.toLowerCase().trim())}
                    className="w-full pl-11 pr-4 py-3.5 bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                    placeholder="e.g. acme-corp"
                  />
                  {isCheckingSso && (
                    <div className="absolute inset-y-0 right-4 flex items-center">
                      <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              {/* SSO Button */}
              {ssoStatus?.ssoEnabled && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  <button
                    type="button"
                    onClick={handleSsoLogin}
                    disabled={isSubmitting}
                    className="w-full py-3.5 px-4 bg-blue-500/10 border border-blue-500/30 text-blue-400 font-bold rounded-xl hover:bg-blue-500/20 transition-all flex items-center justify-center gap-3 group"
                  >
                    <Shield className="w-5 h-5 group-hover:scale-110 transition-transform" />
                    Sign in with SSO
                  </button>
                  {ssoStatus.allowPasswordLogin && !ssoStatus.enforceSso && (
                    <div className="flex items-center my-6">
                      <div className="flex-grow border-t border-white/5"></div>
                      <span className="mx-4 text-[10px] text-slate-600 font-bold uppercase tracking-widest">OR</span>
                      <div className="flex-grow border-t border-white/5"></div>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Password Login */}
              {(!ssoStatus?.enforceSso || !ssoStatus?.ssoEnabled) && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Username</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
                        <UserIcon className="w-4 h-4" />
                      </div>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="w-full pl-11 pr-4 py-3.5 bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                        placeholder="Enter username"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Password</label>
                      <Link to="/forgot-password" className="text-xs font-bold text-blue-500 hover:text-blue-400">Forgot?</Link>
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
                        <Lock className="w-4 h-4" />
                      </div>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-11 pr-12 py-3.5 bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 transition-all"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-600 hover:text-slate-400 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <p className="font-medium">{error}</p>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-blue-600 to-violet-600 text-white font-bold rounded-xl shadow-xl shadow-blue-600/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 group"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
                      Sign In <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>}
                  </button>
                </div>
              )}
            </form>

            <div className="mt-10 pt-8 border-t border-white/5 flex flex-col items-center gap-4">
              <div className="flex items-center gap-2 text-slate-600 text-[10px] font-bold uppercase tracking-widest">
                <Shield className="w-3 h-3 text-emerald-500" />
                Enterprise Security V4.2.0
              </div>
              <Link to="/" className="text-xs font-semibold text-slate-500 hover:text-slate-300 transition-colors no-underline">← Back to the AxiomFlow home page</Link>
              <span className="text-[10px] text-slate-600">AxiomFlow is a product of AnjaniGiri.</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginScreen;
